import React, { Fragment, useMemo, useState, useEffect } from 'react';
import { LoadingButton } from '@mui/lab';
import { Button, Stack } from '@mui/material';
import { invalidFields, validationTypes } from 'components/FormCreator/types';
import { setListOfInvalids, validateFields } from 'components/FormCreator/validation';
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import _ from 'lodash';

// Services
import { validateStep2 } from 'services/balance.services';
import { getActivitiesAsync } from 'services/activity.services';
import { getAltshulerAccountsByCompanyId } from 'services/bank-account.services';
import { getDailyDigitalBalance } from 'services/activity.services';

// Redux
import { getTranfserAmount, isLoading, setTransferAmount, getAddBalanceState } from 'state/reducers/balance.reducers';
import { selectTranslations } from 'state/reducers/translation.reducers';
import { getUserDetails } from 'state/reducers/user.reducers';
import { getActivities } from 'state/reducers/activity.reducers';
import { getAltshulerAccounts } from 'state/reducers/bank-account.reducers';
import { getMobile } from 'state/reducers/ui.reducers';

// Constants
import { balanceSteps, minimumNewBalanceAmount, BalanceDepositType } from 'constants/balance.constants';

// Components
import { FormButton } from 'theme/elements/Buttons';
import CurrencyApprovalForm from '../../../manual/steps/step2/CurrencyApprovalForm';
import DepositAmountForm from '../../../digital/steps/step2/DepositAmountForm';
import { getDomainSettings } from 'state/reducers/system_settings.reducers';

// Separate validation rules for each process type
export const digitalFieldsValidations = {
	amount: [validationTypes.requierd, validationTypes.minAmount, validationTypes.maxAmount],
};

export const manualFieldsValidations = {
	amount: [validationTypes.requierd, validationTypes.minAmount],
};

export default function Step2(props: { handleCancel: any; handleTransferProofFile: any; handleBack: any }) {
	const t = useAppSelector(selectTranslations);
	const dispatch = useAppDispatch();
	const balanceState = useAppSelector(getAddBalanceState);
	const [invalidFields, setInvalidFields] = useState<invalidFields>({});
	const loading = useAppSelector(isLoading);
	const amount = useAppSelector(getTranfserAmount);
	const user = useAppSelector(getUserDetails);
	const activities = useAppSelector(getActivities);
	const altshulerAccounts = useAppSelector(getAltshulerAccounts);
	const isMobile = useAppSelector(getMobile);
	const balanceSettings = useAppSelector(getDomainSettings('balance'));
	const maxAmountToDigitalDeposit = balanceSettings?.maxAmountToDigitalDeposit || 0;

	const activeFieldsValidations = useMemo(() => {
		return balanceState === BalanceDepositType.Digital ? digitalFieldsValidations : manualFieldsValidations;
	}, [balanceState]);

	// Calculate available amount for digital deposits
	const availableAmount = useMemo(() => {
		if (balanceState === BalanceDepositType.Digital) {
			return maxAmountToDigitalDeposit - getDailyDigitalBalance();
		}
		return maxAmountToDigitalDeposit;
	}, [balanceState, maxAmountToDigitalDeposit]);

	// Manual-specific state
	const [isFileUploaded, setIsFileUploaded] = useState(false);

	// Digital-specific API calls
	useEffect(() => {
		if (balanceState === BalanceDepositType.Digital) {
			const yesterday = new Date();
			yesterday.setDate(yesterday.getDate() - 1);
			dispatch(getActivitiesAsync(Number(user?.companyId), yesterday, new Date()));
		}
	}, [user?.companyId]);

	useEffect(() => {
		if (balanceState === BalanceDepositType.Digital && (!altshulerAccounts || altshulerAccounts.length === 0)) {
			dispatch(getAltshulerAccountsByCompanyId());
		}
	}, [altshulerAccounts]);

	// Dynamic continue button logic
	const isContinueDisabled = useMemo(() => {
		const baseCondition = loading || _.isNil(amount) || !!_.keys(invalidFields).length;

		if (balanceState === 'manual') {
			return baseCondition || !isFileUploaded;
		}

		return baseCondition;
	}, [loading, amount, invalidFields, isFileUploaded, balanceState]);

	// Conditionally build validation data - only include maxAmount for digital processes
	const validationData: Record<validationTypes, string | number | boolean> = useMemo(() => {
		const baseData = {
			[validationTypes.minAmount]: minimumNewBalanceAmount,
		} as Record<validationTypes, string | number | boolean>;

		if (balanceState === BalanceDepositType.Digital) {
			baseData[validationTypes.maxAmount] = availableAmount;
		}

		return baseData;
	}, [balanceState, availableAmount]);

	const handleCurrencyApproval = () => {
		let obj = {};
		_.keys(activeFieldsValidations).forEach((key) => {
			obj = { ...obj, [key]: amount };
		});

		const invalidFields = validateFields(activeFieldsValidations, obj, validationData);
		if (!_.keys(invalidFields).length) {
			dispatch(validateStep2());
		} else {
			setInvalidFields(invalidFields);
		}
	};

	// Manual-specific file upload handler
	const handleFileUpload = (file: File | null) => {
		if (!file) {
			setIsFileUploaded(false);
		} else {
			setIsFileUploaded(true);
		}
		props.handleTransferProofFile(file);
	};

	const handleFormChange = ({ name, value }) => {
		if (name === 'amount') {
			try {
				dispatch(setTransferAmount(value));
			} catch (error) {
				dispatch(setTransferAmount('0.00'));
			}
		}

		if (_.has(activeFieldsValidations, name)) {
			setInvalidFields(setListOfInvalids(invalidFields, activeFieldsValidations, name, value, validationData));
		}
	};

	// Dynamic translation keys
	const getTranslationKey = (suffix: string) => {
		const prefix =
			balanceState === BalanceDepositType.Digital ? 'page.balance.add.digital.step.2' : 'page.balance.add.step.2';
		return `${prefix}.${suffix}`;
	};

	return (
		<Fragment>
			<Stack spacing={4}>
				{balanceState === 'manual' ? (
					<CurrencyApprovalForm
						handleTransferProofFile={handleFileUpload}
						isFileUploaded={isFileUploaded}
						handleFormChange={handleFormChange}
						invalidFields={invalidFields}
					/>
				) : (
					<DepositAmountForm
						handleFormChange={handleFormChange}
						invalidFields={invalidFields}
						availableAmount={availableAmount}
					/>
				)}
			</Stack>
			<Stack justifyContent="space-between" direction="row" marginTop="32px">
				{!isMobile && (
					<FormButton>
						<Button disabled={loading} variant="text" onClick={props.handleCancel}>
							{t(getTranslationKey('cancel_button.text'))}
						</Button>
					</FormButton>
				)}
				<Stack justifyContent="space-between" direction="row">
					<FormButton>
						<Button variant="outlined" onClick={() => props.handleBack(balanceSteps.BankAccount)}>
							{t(getTranslationKey('back_button.text'))}
						</Button>
					</FormButton>
					<FormButton sx={{ marginLeft: '8px' }}>
						<LoadingButton
							variant="contained"
							disabled={isContinueDisabled}
							onClick={handleCurrencyApproval}
						>
							{t(getTranslationKey('continue_button.text'))}
						</LoadingButton>
					</FormButton>
				</Stack>
			</Stack>
		</Fragment>
	);
}
